import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  DatePicker,
  Form,
  FormField,
  FormRow,
  FormSection,
  FormTitle,
  Input,
  ModalFooter,
  Select,
  Text,
  View,
} from '../ui';
import { Controller, useForm } from 'react-hook-form';
import * as zod from 'zod';
import LocationSelect, { RegionSelect } from '../LocationSelect';
import startCase from 'lodash/startCase';
import moment from 'moment';
import type { VesselReservationFragment } from '../../graphql';
import {
  useCreateVesselReservationMutation,
  useUpdateVesselReservationMutation,
  VesselReservationType,
} from '../../graphql';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import { vesselReservationTypes } from '../../constants/vesselReservationTypes';
import { stringToNumber, valueOrNull } from '../../lib/zod-util';
import { useVesselReservationDefaultType } from '../../state/planning/timeline';

const vesselReservationFormSchema = zod.intersection(
  zod.object({
    type: zod.nativeEnum(VesselReservationType),
    startTime: zod.string(),
    expireTime: zod.string().nullable(),
    duration: zod.number(),
    remark: zod.string().optional().nullable(),
  }),
  zod.union([
    zod.object({
      startPortId: zod.number(),
      endPortId: zod.number(),
      startRegionId: zod.null(),
      endRegionId: zod.null(),
    }),
    zod.object({
      startPortId: zod.null(),
      endPortId: zod.null(),
      startRegionId: zod.number(),
      endRegionId: zod.number(),
    }),
  ]),
);

type VesselReservationFormFields = zod.infer<
  typeof vesselReservationFormSchema
>;

export interface VesselReservationFormProps {
  onClose: () => void;
  vesselName?: string;
  lastItemEnd?: number;
  vesselReservation?: VesselReservationFragment;
  scheduleContext?: ScheduleDataContext;
}

const VesselReservationForm = ({
  onClose,
  vesselId,
  vesselName,
  vesselReservation,
  scheduleContext,
  lastItemEnd,
}: VesselReservationFormProps & { vesselId: number }) => {
  const [errorMessage, setErrorMessage] = useState('');
  const { locations } = scheduleContext || {};
  const {
    id,
    userByCreatedByUserId,
    shipByVesselId,
    startPortId,
    endPortId,
    startRegionId,
    endRegionId,
    startTime,
    expireTime,
    remark,
    type,
    duration,
  } = vesselReservation || {};

  const [checked, setChecked] = useState(
    startTime
      ? moment(startTime).valueOf() ===
          moment(lastItemEnd).startOf('hour').valueOf()
      : true,
  );
  const [
    createVesselReservationMutation,
    { loading: saveLoading, error: saveError },
  ] = useCreateVesselReservationMutation();
  const [
    updateVesselReservationMutation,
    { loading: updateLoading, error: updateError },
  ] = useUpdateVesselReservationMutation();
  const isLoading = saveLoading || updateLoading;

  const isEdit = !!id;
  const createdBy = userByCreatedByUserId?.name;
  const startPort = startPortId ? locations?.get(startPortId) : null;
  const endPort = endPortId ? locations?.get(endPortId) : null;
  const vName = vesselName ?? shipByVesselId?.name;

  const startEndValues =
    startPortId !== null && endPortId !== null
      ? {
          startPortId,
          endPortId,
          startRegionId: undefined,
          endRegionId: undefined,
        }
      : startRegionId !== null && endRegionId !== null
        ? {
            startRegionId,
            endRegionId,
            startPortId: undefined,
            endPortId: undefined,
          }
        : {};

  const [defaultReservationType, setDefaultReservationType] =
    useVesselReservationDefaultType();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm<VesselReservationFormFields>({
    defaultValues: {
      type: type ?? defaultReservationType,
      startTime: startTime
        ? moment(startTime).format()
        : moment(lastItemEnd).startOf('hour').add(1, 'hour').format(),
      expireTime: expireTime
        ? moment(expireTime).format()
        : moment(lastItemEnd).startOf('hour').add(5, 'day').format(),
      remark: remark ?? undefined,
      duration: duration ?? undefined,
      ...startEndValues,
    },
  });

  const errorKeys = Object.keys(errors);
  const saveErrors = saveError || updateError;
  const currentType = watch('type');

  useEffect(() => {
    if (currentType !== defaultReservationType) {
      setDefaultReservationType(currentType);
    }
  }, [currentType, defaultReservationType, setDefaultReservationType]);

  const onSubmit = async (data: VesselReservationFormFields) => {
    try {
      const input = {
        startTime: data.startTime,
        expireTime: data.expireTime,
        startPortId: data.startPortId,
        endPortId: data.endPortId,
        startRegionId: data.startRegionId,
        endRegionId: data.endRegionId,
        remark: data.remark,
        type: data.type,
        duration: data.duration,
      };

      if (data.type === VesselReservationType.TRADE_LANE) {
        input.startPortId = input.endPortId = input.expireTime = null;
      } else {
        input.startRegionId = input.endRegionId = null;
      }

      const parsed = vesselReservationFormSchema.parse(input);
      const parsedInput = {
        ...parsed,
        vesselId: vesselReservation?.vesselId ?? vesselId,
      };

      const id = vesselReservation?.id;
      if (id) {
        const result = await updateVesselReservationMutation({
          variables: {
            input: {
              id,
              vesselReservationPatch: parsedInput,
            },
          },
        });
        if (!result.errors?.length) {
          onClose();
        }
      } else {
        const result = await createVesselReservationMutation({
          variables: {
            input: {
              vesselReservation: parsedInput,
            },
          },
        });
        if (!result.errors?.length) {
          onClose();
        }
      }
    } catch (e) {
      setErrorMessage(e.message);
    }
  };

  return (
    <View height="100%">
      <Form
        style={{ overflowY: 'visible', borderBottom: 'none' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormSection
          borderBottomStyle="none"
          marginTop={2}
          marginLeft={-2}
          marginBottom={-3}
        >
          <FormTitle>
            <Text fontSize="1.4em" fontWeight="bold">
              {`${isEdit ? 'Edit' : 'New'} reservation for (${vName})`}
            </Text>
          </FormTitle>
        </FormSection>
        <FormSection zIndex={4} borderBottomStyle="none">
          <FormRow>
            <FormField
              label="Type"
              width="49%"
              required
              hasError={!!errors.type}
            >
              <Select
                data-testid="type-dropdown"
                {...register('type')}
                options={vesselReservationTypes}
                placeholder="Select type"
                required
              />
            </FormField>

            <FormField
              required={!checked}
              label="Start time"
              width="48%"
              hasError={!!errors.startTime}
            >
              <Box as="label" display="flex" alignItems="center">
                <input
                  style={{ margin: '10px 5px 10px 0' }}
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    setChecked(!checked);
                    setValue(
                      'startTime',
                      moment(lastItemEnd).startOf('hour').format(),
                    );
                  }}
                />
                Right after previous voyage
              </Box>
            </FormField>
          </FormRow>

          <FormRow>
            <FormField
              required={!checked}
              label={checked ? 'Start time' : 'Start time override'}
              width="37%"
              hasError={!!errors.startTime}
            >
              <Controller
                control={control}
                name="startTime"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    disabled={checked}
                    onChange={(date) => onChange(date?.toISOString())}
                    selected={value ? new Date(value) : null}
                    showTime
                    showTimeInput
                    timeZone={startPort?.timezone ?? 'UTC'}
                  />
                )}
                rules={{ required: true }}
              />
            </FormField>

            <FormField
              label="Duration(days)"
              width="22%"
              hasError={!!errors.duration}
              required
            >
              <Input
                {...register('duration', { setValueAs: stringToNumber })}
                type="number"
                required
                min={0}
                step={1}
              />
            </FormField>

            {currentType !== VesselReservationType.TRADE_LANE && (
              <FormField
                label="Expires on"
                data-testid="expires-on-input"
                width="37%"
                required
                hasError={!!errors.expireTime}
              >
                <Controller
                  control={control}
                  name="expireTime"
                  render={({ field: { value, onChange } }) => (
                    <DatePicker
                      onChange={(date) => onChange(date?.toISOString())}
                      selected={value ? new Date(value) : null}
                      showTime
                      showTimeInput
                      timeZone={endPort?.timezone ?? 'UTC'}
                    />
                  )}
                  rules={{ required: true }}
                />
              </FormField>
            )}
          </FormRow>
          {currentType !== VesselReservationType.TRADE_LANE && (
            <>
              <FormRow>
                <FormField
                  data-testid="start port"
                  label="Start port"
                  width="49%"
                  required
                  hasError={!!errors.startPortId}
                >
                  <Controller
                    control={control}
                    name="startPortId"
                    render={({ field: { value, onChange } }) => (
                      <LocationSelect
                        value={{ value }}
                        onChange={(newValue) => onChange(newValue?.value)}
                      />
                    )}
                    rules={{ required: true }}
                  />
                </FormField>

                <FormField
                  data-testid="end port"
                  label="End port"
                  width="48%"
                  required
                  hasError={!!errors.endPortId}
                >
                  <Controller
                    control={control}
                    name="endPortId"
                    render={({ field: { value, onChange } }) => (
                      <LocationSelect
                        value={{ value }}
                        onChange={(newValue) => onChange(newValue?.value)}
                      />
                    )}
                    rules={{ required: true }}
                  />
                </FormField>
              </FormRow>
            </>
          )}
          {currentType === VesselReservationType.TRADE_LANE && (
            <>
              <FormRow>
                <FormField
                  data-testid="start region"
                  label="Start region"
                  width="49%"
                  required
                  hasError={!!errors.startRegionId}
                >
                  <Controller
                    control={control}
                    name="startRegionId"
                    render={({ field: { value, onChange } }) => (
                      <RegionSelect
                        value={{ value }}
                        onChange={(newValue) => onChange(newValue?.value)}
                      />
                    )}
                    rules={{ required: true }}
                  />
                </FormField>

                <FormField
                  data-testid="end region"
                  label="End region"
                  width="48%"
                  required
                  hasError={!!errors.endRegionId}
                >
                  <Controller
                    control={control}
                    name="endRegionId"
                    render={({ field: { value, onChange } }) => (
                      <RegionSelect
                        value={{ value }}
                        onChange={(newValue) => onChange(newValue?.value)}
                      />
                    )}
                    rules={{ required: true }}
                  />
                </FormField>
              </FormRow>
            </>
          )}

          <FormRow>
            <FormField label="Remarks" width="98%" hasError={!!errors.remark}>
              <Input
                {...register('remark', { setValueAs: valueOrNull })}
                type="text"
              />
            </FormField>
          </FormRow>
          <FormRow mt={4}>
            {createdBy ? (
              <FormField label="Created by" width="49%">
                <Text>{createdBy}</Text>
              </FormField>
            ) : null}
          </FormRow>
        </FormSection>

        {errorKeys.length > 0 && (
          <FormSection borderBottomStyle="none">
            <Text color="#9d0300">
              {errorKeys
                .map((key) => `${startCase(key)} is not valid.`)
                .join(' ')}
            </Text>
          </FormSection>
        )}

        {errorMessage.length > 0 && (
          <FormSection borderBottomStyle="none">
            <Text color="#9d0300">{errorMessage}</Text>
          </FormSection>
        )}

        {isLoading && (
          <FormSection borderBottomStyle="none">
            <View flexDirection="row">
              <Text mr={3}>Saving changes</Text>
            </View>
          </FormSection>
        )}

        {saveErrors && (
          <FormSection borderBottomStyle="none">
            <Text color="#9d0300">{saveErrors.name}</Text>
            <Text color="#9d0300">{saveErrors.message}</Text>
            <Text color="#9d0300">
              {saveErrors.clientErrors
                .map((e) => `${e.name}: ${e.message}`)
                .join(' \n')}
            </Text>
            <Text color="#9d0300">
              {saveErrors.graphQLErrors
                .map((e) => `${e.name}: ${e.message}`)
                .join(' \n')}
            </Text>
          </FormSection>
        )}
      </Form>

      <div style={{ flexGrow: 1 }} />

      <ModalFooter>
        {onClose && (
          <Button
            variant="negative"
            disabled={isLoading}
            onPress={onClose}
            marginRight="3"
          >
            Discard changes & Close
          </Button>
        )}
        <div style={{ flexGrow: 1 }} />
        <Button disabled={isLoading} onPress={handleSubmit(onSubmit)}>
          Save & Close
        </Button>
      </ModalFooter>
    </View>
  );
};

export default VesselReservationForm;
